import { JSX, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";

import ActionButton from "lib/components/button/ActionButton";
import useListActionInfo, { ListActionInfo } from "lib/list/useListActionInfo";

const BUTTON_SIZE = "large";

export type TrashActionbarProps = {
	onRestore: (ids: Array<string>) => Promise<void>;
	onDelete: (ids: Array<string>) => Promise<void>;
};

export default function TrashActionbar(
	props: Readonly<TrashActionbarProps>
): JSX.Element {
	const { t } = useTranslation();

	const listInfo: ListActionInfo = useListActionInfo();

	const handleRestore = useCallback(
		(): Promise<void> => props.onRestore(listInfo.selected),
		[props.onRestore, listInfo.selected]
	);

	const handleDelete = useCallback(
		(): Promise<void> => props.onDelete(listInfo.selected),
		[props.onDelete, listInfo.selected]
	);

	return (
		<Grid
			container
			alignItems="center"
			sx={{
				width: "fit-content",
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				backgroundColor: "background.paper",
				"& hr": {
					my: 0,
					mx: 0.5
				}
			}}>
			<ActionButton
				icon={<RestoreFromTrashOutlinedIcon />}
				tooltip={t("restore")}
				disabled={listInfo.noneSelected}
				size={BUTTON_SIZE}
				dialogTitle={t("restoreTrash")}
				dialogText={t("restoreTrashMessage")}
				dialogCancelLabel={t("cancel")}
				dialogConfirmLabel={t("restore")}
				onAction={handleRestore}
			/>
			<Divider orientation="vertical" flexItem />
			<ActionButton
				icon={<DeleteForeverOutlinedIcon />}
				tooltip={t("delete")}
				disabled={listInfo.noneSelected}
				size={BUTTON_SIZE}
				dialogTitle={t("deleteTrash")}
				dialogText={t("deleteTrashMessage")}
				dialogCancelLabel={t("cancel")}
				dialogConfirmLabel={t("delete")}
				onAction={handleDelete}
			/>
		</Grid>
	);
}
